import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "../lib/utils"
import { overlayBackdrop, overlaySurface } from "../styles/recipes.js"
import {
  OverlayFocusContext,
  restoreOverlayFocus,
  useOverlayFocus,
} from "../lib/controlled-overlay-focus.js"
import { PortalContainerContext } from "./portal-container.jsx"

/** Normalize Radix outside events: target may be a Text node; use native event target when present. */
function getOutsideEventDomTarget(event) {
  const fromDetail = event.detail?.originalEvent?.target
  const raw = fromDetail ?? event.target
  if (!raw) return null
  if (raw instanceof Element) return raw
  if (raw instanceof Text && raw.parentElement) return raw.parentElement
  return null
}

/** react-select (classNamePrefix "react-select") portals the menu to `body`; Radix Dialog treats those clicks as "outside". */
function isReactSelectPortalTarget(node) {
  if (!node || typeof node !== "object") return false
  const el = node instanceof Element ? node : node instanceof Text ? node.parentElement : null
  if (!el) return false
  return Boolean(
    el.closest(".react-select__menu") || el.closest(".react-select__menu-portal"),
  )
}

function Dialog({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  ...props
}) {
  const { focusContext, handleOpenChange } = useOverlayFocus(open, defaultOpen, onOpenChange, modal)
  return (
    <OverlayFocusContext.Provider value={focusContext}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
        {...props}>
        {children}
      </DialogPrimitive.Root>
    </OverlayFocusContext.Provider>
  )
}

const DialogTrigger = React.forwardRef(({ onClick, ...props }, ref) => {
  const focusContext = React.useContext(OverlayFocusContext)
  return (
    <DialogPrimitive.Trigger
      ref={ref}
      data-slot="dialog-trigger"
      {...props}
      onClick={(event) => {
        focusContext?.captureInvoker(event.currentTarget)
        onClick?.(event)
      }} />
  )
});
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName;

function DialogPortal({
  container,
  ...props
}) {
  const portalContainer = React.useContext(PortalContainerContext)
  return <DialogPrimitive.Portal container={container === undefined ? portalContainer : container} data-slot="dialog-portal" {...props} />;
}

const DialogClose = React.forwardRef(({ ...props }, ref) => (
  <DialogPrimitive.Close ref={ref} data-slot="dialog-close" {...props} />
));
DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogOverlay = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-[var(--ds-layer-dialog-backdrop)] ${overlayBackdrop}`,
      className
    )}
    {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(({
  className,
  container,
  overlayClassName,
  children,
  showCloseButton = true,
  closeLabel,
  onCloseAutoFocus,
  onInteractOutside,
  onFocusOutside,
  onPointerDownOutside,
  ...props
}, ref) => {
  const { t } = useTranslation('common')
  const focusContext = React.useContext(OverlayFocusContext)
  const resolvedCloseLabel = closeLabel ?? t('actions.close', { defaultValue: 'Close' })
  const handleCloseAutoFocus = (event) => {
    onCloseAutoFocus?.(event)
    restoreOverlayFocus(event, focusContext)
  }
  const handlePointerDownOutside = (event) => {
    if (isReactSelectPortalTarget(getOutsideEventDomTarget(event))) {
      event.preventDefault()
      return
    }
    onPointerDownOutside?.(event)
  }

  const handleInteractOutside = (event) => {
    if (isReactSelectPortalTarget(getOutsideEventDomTarget(event))) {
      event.preventDefault()
      return
    }
    onInteractOutside?.(event)
  }

  const handleFocusOutside = (event) => {
    const original = event.detail?.originalEvent
    const related =
      original && "relatedTarget" in original ? original.relatedTarget : null
    if (
      isReactSelectPortalTarget(getOutsideEventDomTarget(event)) ||
      isReactSelectPortalTarget(related)
    ) {
      event.preventDefault()
      return
    }
    onFocusOutside?.(event)
  }

  return (
    <DialogPortal container={container} data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          `text-card-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-[var(--ds-layer-dialog)] grid w-full max-w-[var(--ds-overlay-dialog-default-width)] translate-x-[-50%] translate-y-[-50%] gap-[var(--ds-space-4)] overflow-hidden ${overlaySurface} p-[var(--ds-space-6)]`,
          className
        )}
        onCloseAutoFocus={handleCloseAutoFocus}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onFocusOutside={handleFocusOutside}
        {...props}>
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-foreground absolute top-[var(--ds-space-4)] right-[var(--ds-space-4)] rounded-[var(--ds-radius-control)] opacity-70 transition-opacity hover:bg-accent hover:opacity-100 focus-visible:ring-[var(--ds-focus-ring-width)] focus-visible:ring-offset-[var(--ds-focus-ring-offset)] focus-visible:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[var(--ds-icon-size-sm)]">
            <XIcon />
            <span className="sr-only">{resolvedCloseLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <div
    ref={ref}
    data-slot="dialog-header"
    className={cn("flex flex-col gap-[var(--ds-space-2)] text-center sm:text-left", className)}
    {...props} />
));
DialogHeader.displayName = "DialogHeader";

const DialogFooter = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <div
    ref={ref}
    data-slot="dialog-footer"
    className={cn("flex flex-col-reverse gap-[var(--ds-space-2)] sm:flex-row sm:justify-end", className)}
    {...props} />
));
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    data-slot="dialog-title"
    className={cn("text-lg leading-none font-semibold tracking-[-0.02em]", className)}
    {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot="dialog-description"
    className={cn("text-muted-foreground text-sm leading-5", className)}
    {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
